import { collab } from 'prosemirror-collab'
import { EditorState } from 'prosemirror-state'

import { ArticleFlat, ElementPresence, RootState, Comment, AnonymousUser, Layer, Auth } from 'types'
import { getEditPlugins, PluginsStores } from 'common/Prose/Plugins/utils/getEditPlugins'
import { InlinePresencePluginKey } from 'proseShared/plugins/InlinePresence/InlinePresence'
import { DocPresence } from 'stores/DocPresence'
import { getDocumentFromJson } from 'proseShared/utils/state'
import { nextColor } from 'common/PresenceCursor/PresenceCursor'
import { consolidatedUserId } from 'common/PresenceAvatars/consolidatedUserId'
import { schema } from 'prose-core'

import store from 'redux/store'
import { upsertUserColor } from 'redux/actions'
import { includes } from 'lodash'
import { QueryClient } from 'react-query'
import { AuthStore } from 'stores/AuthStore'
import { Step } from 'prosemirror-transform'
import { IStackManager } from 'almanac-ui/Modal/Types'

export interface StateFromDocParams {
  auth?: Auth | null
  authStore?: AuthStore | null
  queryClient: QueryClient
  article: ArticleFlat
  layer: Layer
  clientUuid: string
  commentOnly: boolean
  readOnly: boolean
  isMobile: boolean
  disabledPlugins?: string[]
  features?: string[] | undefined
  stores?: PluginsStores
  stackManager: IStackManager
}

export const createStateFromDoc = ({
  auth,
  authStore,
  queryClient,
  article,
  layer,
  clientUuid,
  commentOnly,
  readOnly,
  isMobile,
  disabledPlugins = [],
  features,
  stores = {},
  stackManager,
}: StateFromDocParams) => {
  const initElements = article.is_crdt ? [] : layer.elements
  const doc = getDocumentFromJson(initElements, schema, !article.is_crdt)

  const plugins = [
    ...getEditPlugins({
      auth,
      authStore,
      schema,
      queryClient,
      article,
      commentOnly,
      readOnly,
      isMobile,
      layer,
      features,
      stores,
      stackManager,
    }),
    collab({ version: layer.current_version + 1, clientID: clientUuid }),
  ]

  /*
   * Filter keys for custom plugins look like mentionPrompt$
   * Filter keys for existing plugins look like plugin$0, plugin$1, ...
   * We don't want to filter generic keys (plugin) to not accidentally
   * deactivate all of them
   */
  const filteredPlugins = plugins.filter((plugin: any) => {
    const filterKey = plugin.key.split('$')[0]
    return filterKey !== 'filter' && !includes(disabledPlugins, filterKey)
  })

  let snapshotState = EditorState.create({ doc })
  if (!readOnly && layer.last_snapshot_delta_steps) {
    const { tr } = snapshotState
    layer.last_snapshot_delta_steps.forEach((stepRow) => {
      const step = Step.fromJSON(schema, stepRow.data)
      tr.step(step)
    })
    snapshotState = snapshotState.apply(tr)
  }

  return EditorState.create({ doc: snapshotState.doc, plugins: filteredPlugins })
}

export type CommentChannelReceivePayload = {
  comment: Comment
  replies: []
  action: 'add' | 'remove'
  comment_id: number
  marker_id: number
}

export const ELEMENT_PRESENCE_WS_CHANNEL = 'ElementPresenceChannel'

const pushColorsToRedux = ({ presences, articleId }: { presences: ElementPresence[]; articleId: string }) => {
  presences.forEach((presence) => {
    const { color, user, anonymous_user } = presence

    if (!color) {
      const currentColors = presences.map(({ color }) => color).filter((c) => c) as string[]
      presence.color = nextColor(currentColors)

      const userId = consolidatedUserId(user || anonymous_user)

      const payload = {
        [articleId]: { [userId]: presence.color },
      }

      store.dispatch(upsertUserColor(payload))
    }
  })
}

export const getElementPresenceUpdateTx = ({
  state,
  articleId,
  auth,
  anonymousUser,
  docPresenceState,
  elementPresence,
  layerId,
}: {
  state?: EditorState
  articleId: string
  auth?: RootState['auth']
  anonymousUser?: RootState['anonymousUser']
  elementPresence: ElementPresence | null
  docPresenceState: DocPresence | null
  layerId: string
}) => {
  if (!state) return
  if (!auth && !anonymousUser) return
  if (!elementPresence) return

  const userId = elementPresence.user?.id || elementPresence.anonymous_user?.uuid
  const userIsMe = userId && userId === (auth ? auth.id : (anonymousUser as AnonymousUser).uuid)
  if (userIsMe) return

  const pluginState = InlinePresencePluginKey.getState(state)
  if (!pluginState) return

  const presences = mergePresence(elementPresence, pluginState.presences as ElementPresence[])

  if (docPresenceState) {
    docPresenceState.updatePresences(
      presences.filter((presence) => presence.head),
      layerId,
    )
  }
  pushColorsToRedux({ articleId, presences })

  const { tr } = state
  tr.setMeta(InlinePresencePluginKey, presences)
  tr.setMeta('realTime', true)
  return tr
}

export const mergePresence = (presence: ElementPresence, presences: ElementPresence[]) => {
  const { user, anonymous_user } = presence

  const hasSameId = (pres: ElementPresence) => {
    return user ? user.id === pres.user?.id : anonymous_user?.uuid === pres.anonymous_user?.uuid
  }

  // existing => changed pos, update
  const existing = presences.find((pres) => hasSameId(pres))
  if (existing) {
    return presences.map((pres) => {
      if (hasSameId(pres)) return { ...pres, ...presence }

      return pres
    })
  }

  // non existing => add
  return [...presences, presence]
}

export const getCollabClientId = (state: EditorState) => {
  // Ignore typescript to access private 'config' field
  //@ts-ignore
  return state.config.pluginsByKey['collab$'].spec.config.clientID
}
